import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Activity,
  Avatar,
  AvatarGroup,
  Button,
  Clickable,
  Dot,
  ForEach,
  Icon,
  If,
  MessageDisplay,
  MessageEditor,
  MessageEditorSubmitParams,
  P,
  Stack,
  useRelativeTime,
} from '@fashionnovateam/nova-ui-toolkit';
import { Client } from '@twilio/conversations';
import cn from 'classnames';
import Page from '../Page';
import Header from '../Header';
import {
  ChatConversation,
  ChatMessage,
  ChatMessageTiptapAttributes,
  ChatParticipant,
  ChatProvider,
  useChatContext,
  useChatConversations,
  useChatMessages,
  useConversationBySid,
  useLastMessage,
  useParticipantFromMessage,
  useParticipants,
  useUserFromParticipant,
} from '../Chat';
import { PageLoading } from '../PageLoading/PageLoading';

export type HiddenChatProps = {
  userId: string;
};

function ConversationViewInput(props: { conversation: ChatConversation }) {
  const { conversation } = props;

  const sendMessage = useCallback(
    async (params: MessageEditorSubmitParams) => {
      const attributes: ChatMessageTiptapAttributes = {
        type: 'tiptap',
        preview: params.text.slice(0, 50),
      };

      const message = conversation.instance
        .prepareMessage()
        .setBody(JSON.stringify(params.json))
        .setAttributes(attributes);

      params.files.forEach((file, index) => {
        const formData = new FormData();
        formData.append(`file-${index + 1}`, file, file.name);
        message.addMedia(formData);
      });

      await message.build().send();
    },
    [conversation.instance],
  );

  return (
    <div className="p-4 bg-neutral-100 border-t border-neutral-300 w-full">
      <MessageEditor onSubmit={sendMessage} />
    </div>
  );
}

function ConversationViewMessage(props: {
  message: ChatMessage;
  isLast: boolean;
}) {
  const { message, isLast } = props;
  const { userId, identity } = useChatContext();

  const participantProcess = useParticipantFromMessage(
    userId,
    message.instance,
  );
  const userProcess = useUserFromParticipant(
    userId,
    participantProcess.data?.instance,
  );

  const isVendor =
    userProcess?.data?.attributes.type === 'vp' &&
    ['VENDOR', 'VENDOR_STAFF'].includes(userProcess.data.attributes.role);
  const isInfluencer =
    userProcess?.data?.attributes.type === 'vp' &&
    ['INFLUENCER'].includes(userProcess.data.attributes.role);
  const isInternal =
    userProcess?.data?.attributes.type === 'internal' ||
    (userProcess?.data?.attributes.type === 'vp' &&
      !['VENDOR', 'VENDOR_STAFF', 'INFLUENCER'].includes(
        userProcess.data.attributes.role,
      ));

  const badge =
    (isVendor && 'vendor') ||
    (isInfluencer && 'influencer') ||
    (isInternal && 'fn_internal') ||
    undefined;

  return (
    <Activity
      name={userProcess.data?.friendlyName || ''}
      badgeType={badge}
      hideBar={isLast}
    >
      <Activity.Message
        name={userProcess.data?.friendlyName || ''}
        date={message.instance.dateCreated || new Date()}
        isPersonal={identity === participantProcess.data?.identity}
      >
        {message.attributes.type === 'tiptap' ? (
          <MessageDisplay content={message.body} files={message.files} />
        ) : (
          <P>{message.body}</P>
        )}
      </Activity.Message>
    </Activity>
  );
}

function ConversationViewMessages(props: { conversation: ChatConversation }) {
  const { conversation } = props;

  const { userId } = useChatContext();

  const messagesProcess = useChatMessages(userId, conversation.instance);
  const lastMessageProcess = useLastMessage(userId, conversation.instance);

  useEffect(() => {
    if (
      lastMessageProcess.data?.instance.index &&
      (!conversation.instance.lastReadMessageIndex ||
        conversation.instance.lastReadMessageIndex <
          lastMessageProcess.data.instance.index)
    ) {
      conversation.instance.updateLastReadMessageIndex(
        lastMessageProcess.data.instance.index,
      );
    }
  }, [
    lastMessageProcess.data?.instance.index,
    conversation.instance.lastReadMessageIndex,
  ]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef(false);

  useEffect(() => {
    if (!scrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    if (!scrollRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [messagesProcess.messages]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 5; // Tolerance of 5px
    scrollRef.current = !isAtBottom;
  };

  if (messagesProcess.isLoading) {
    return (
      <div className="flex-grow overflow-y-auto">
        <Icon.LoadingCircle />
      </div>
    );
  }

  if (messagesProcess.error || !messagesProcess.data) {
    return (
      <P color="error" className="flex-grow overflow-y-auto">
        {messagesProcess.error instanceof Error
          ? messagesProcess.error.message
          : 'Failed to retrieve participants'}
      </P>
    );
  }

  return (
    <div
      className="flex-grow overflow-y-auto p-4 px-8 flex flex-col gap-1"
      onScroll={handleScroll}
    >
      {messagesProcess.hasPreviousPage && (
        <div className="w-full flex justify-center p-4">
          <Button
            loading={messagesProcess.isFetchingPreviousPage}
            onClick={() => messagesProcess.fetchPreviousPage()}
          >
            Load more messages
          </Button>
        </div>
      )}
      <ForEach items={messagesProcess.messages}>
        {(message, index) => (
          <ConversationViewMessage
            key={message.sid}
            message={message}
            isLast={index === messagesProcess.messages.length - 1}
          />
        )}
      </ForEach>
      <span ref={messagesEndRef} />
    </div>
  );
}

function ConversationViewParticipant(props: { participant: ChatParticipant }) {
  const { participant } = props;

  const { userId } = useChatContext();

  const userProcess = useUserFromParticipant(userId, participant.instance);

  const isVendor =
    userProcess?.data?.attributes.type === 'vp' &&
    ['VENDOR', 'VENDOR_STAFF'].includes(userProcess.data.attributes.role);
  const isInfluencer =
    userProcess?.data?.attributes.type === 'vp' &&
    ['INFLUENCER'].includes(userProcess.data.attributes.role);
  const isInternal =
    userProcess?.data?.attributes.type === 'internal' ||
    (userProcess?.data?.attributes.type === 'vp' &&
      !['VENDOR', 'VENDOR_STAFF', 'INFLUENCER'].includes(
        userProcess.data.attributes.role,
      ));

  const badge =
    (isVendor && 'vendor') ||
    (isInfluencer && 'influencer') ||
    (isInternal && 'fn_internal') ||
    undefined;

  return (
    <Avatar name={userProcess.data?.friendlyName || ''} badgeType={badge} />
  );
}

function ConversationViewParticipants(props: {
  conversation: ChatConversation;
}) {
  const { conversation } = props;

  const { userId } = useChatContext();

  const participantsProcess = useParticipants(userId, conversation.instance);

  if (participantsProcess.isLoading) {
    return <Icon.LoadingCircle />;
  }

  if (participantsProcess.error || !participantsProcess.data) {
    return (
      <P color="error">
        {participantsProcess.error instanceof Error
          ? participantsProcess.error.message
          : 'Failed to retrieve participants'}
      </P>
    );
  }

  return (
    <AvatarGroup max={5}>
      {participantsProcess.participants.map((p) => (
        <ConversationViewParticipant key={p.sid} participant={p} />
      ))}
    </AvatarGroup>
  );
}

function ConversationView(props: { client: Client; conversationSid: string }) {
  const { client, conversationSid } = props;

  const { userId } = useChatContext();

  const conversationProcess = useConversationBySid(
    userId,
    client,
    conversationSid,
  );

  if (conversationProcess.isLoading) {
    return (
      <div className="w-2/3 h-full">
        <PageLoading />
      </div>
    );
  }

  if (conversationProcess.error || !conversationProcess.data) {
    return (
      <div className="w-2/3 h-full">
        <P color="error">
          {conversationProcess.error instanceof Error
            ? conversationProcess.error.message
            : 'Failed to retrieve conversation'}
        </P>
      </div>
    );
  }

  const conversation = conversationProcess.data;

  return (
    <div className="w-2/3 h-full flex flex-col">
      <div className="p-4 bg-neutral-100 border-b border-neutral-300 flex items-center sticky top-0 z-10 gap-2">
        <P>{conversation.friendlyName}</P>
        <P color="disabled">{conversation.attributes.type}</P>
        <ConversationViewParticipants conversation={conversation} />
      </div>
      <ConversationViewMessages conversation={conversation} />
      <div className="flex-none">
        <ConversationViewInput
          key={conversation.sid}
          conversation={conversation}
        />
      </div>
    </div>
  );
}

function ConversationListItem(props: {
  conversation: ChatConversation;
  selected: boolean;
  onSelect: (sid: string | undefined) => void;
}) {
  const { conversation, selected, onSelect } = props;

  const { userId } = useChatContext();

  const lastMessageProcess = useLastMessage(userId, conversation.instance);
  const participantProcess = useParticipantFromMessage(
    userId,
    lastMessageProcess.data?.instance,
  );
  const userProcess = useUserFromParticipant(
    userId,
    participantProcess.data?.instance,
  );

  const t = useRelativeTime(
    conversation.instance.lastMessage?.dateCreated ||
      conversation.instance.dateCreated ||
      0,
  );

  const preview =
    lastMessageProcess.data?.attributes.type === 'tiptap'
      ? lastMessageProcess.data.attributes.preview
      : lastMessageProcess.data?.body || '';

  return (
    <Clickable
      onClick={() => onSelect(selected ? undefined : conversation.sid)}
      className={cn(
        'p-4 rounded-lg shadow',
        selected ? 'bg-neutral-100' : 'bg-neutral-0',
        'hover:bg-neutral-200',
      )}
    >
      <Stack direction="row" gap="md">
        <P>{conversation.friendlyName}</P>
        <If
          condition={
            conversation.instance.lastMessage?.index &&
            (!conversation.instance.lastReadMessageIndex ||
              conversation.instance.lastReadMessageIndex <
                conversation.instance.lastMessage.index)
          }
        >
          <Dot color="red" />
        </If>
        <P color="disabled">{t}</P>
      </Stack>
      <div>
        <If
          condition={
            !lastMessageProcess.isLoading &&
            !participantProcess.isLoading &&
            !userProcess.isLoading
          }
          else={<Icon.LoadingCircle className="text-base-disabled" />}
        >
          {lastMessageProcess.data ? (
            <P color="disabled">
              <span>{userProcess.data?.friendlyName || '?'}</span>
              {': '}
              <span>
                {preview.length > 49 ? `${preview.slice(0, 49)}...` : preview}
              </span>
            </P>
          ) : (
            <P color="disabled">No messages</P>
          )}
        </If>
      </div>
    </Clickable>
  );
}

function ConversationList(props: {
  client: Client;
  selected: string | undefined;
  onSelect: (sid: string | undefined) => void;
}) {
  const { client, selected, onSelect } = props;

  const { userId } = useChatContext();
  const conversationsProcess = useChatConversations(userId, client);

  return (
    <div className="w-1/3 h-full border-r border-neutral-300 overflow-y-auto">
      <div className="p-4 bg-neutral-100 sticky top-0 z-10">
        <h2 className="text-xl font-semibold">Conversations</h2>
      </div>
      <div className="p-4 flex flex-col gap-4">
        <ForEach items={conversationsProcess.conversations}>
          {(conversation) => (
            <ConversationListItem
              key={conversation.sid}
              conversation={conversation}
              selected={selected === conversation.sid}
              onSelect={onSelect}
            />
          )}
        </ForEach>
        {conversationsProcess.hasNextPage && (
          <div className="w-full flex justify-center">
            <Button
              loading={conversationsProcess.isFetchingNextPage}
              onClick={() => conversationsProcess.fetchNextPage()}
            >
              Load more conversations (next)
            </Button>
          </div>
        )}
        {conversationsProcess.hasPreviousPage && (
          <div className="w-full flex justify-center p-4">
            <Button
              loading={conversationsProcess.isFetchingPreviousPage}
              onClick={() => conversationsProcess.fetchPreviousPage()}
            >
              Load more conversations (prev)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function HiddenChatContent() {
  const chat = useChatContext();
  const [selectedConversation, setSelectedConversation] = useState<
    string | undefined
  >(undefined);

  if (!chat.client || chat.status !== 'connected') {
    return (
      <div>
        <P>Chat Status: {chat.status}</P>
        {chat.error && <P color="error">{chat.error}</P>}
      </div>
    );
  }

  return (
    <div className="h-full w-full flex">
      <ConversationList
        client={chat.client}
        selected={selectedConversation}
        onSelect={setSelectedConversation}
      />
      {selectedConversation ? (
        <ConversationView
          client={chat.client}
          conversationSid={selectedConversation}
        />
      ) : (
        <div className="w-2/3 h-full flex flex-col">
          Please select a conversation
        </div>
      )}
    </div>
  );
}

export function HiddenChat(props: HiddenChatProps) {
  const { userId } = props;

  return (
    <ChatProvider userType="vp" userId={userId}>
      <Page.Container>
        <Header.Major icon="chat/chat-default" title="Hidden Chat" />
        <Page.Content>
          <HiddenChatContent />
        </Page.Content>
      </Page.Container>
    </ChatProvider>
  );
}
